import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../stores/auth';

interface DocumentUploadProps {
  businessId: string;
}

type DocSummary = {
  id: string;
  title: string;
  type: 'faq' | 'pricing' | 'policy' | 'service' | 'general';
  lastUpdated: string;
};

type DocDetail = DocSummary & {
  content: string;
};

export const DocumentUpload: React.FC<DocumentUploadProps> = ({ businessId }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<'faq' | 'pricing' | 'policy' | 'service' | 'general'>('general');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [documents, setDocuments] = useState<DocSummary[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editType, setEditType] = useState<'faq' | 'pricing' | 'policy' | 'service' | 'general'>('general');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  const { token } = useAuthStore();

  const fetchDocuments = async () => {
    if (!businessId) return;
    setDocsLoading(true);
    setDocsError('');

    try {
      const response = await fetch(`/api/business-docs/list?businessId=${businessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load documents');
      }

      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error(error);
      setDocsError('Failed to load documents');
    } finally {
      setDocsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [businessId, token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch('/api/business-docs/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId,
          title,
          content,
          type,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to upload document');
      }

      await response.json();
      setMessage('✅ Document uploaded successfully!');
      setTitle('');
      setContent('');
      await fetchDocuments();
    } catch (error) {
      setMessage('❌ Error uploading document');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (docId: string) => {
    setEditOpen(true);
    setEditId(docId);
    setEditError('');
    setEditLoading(true);

    try {
      const response = await fetch(`/api/business-docs/${docId}?businessId=${businessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load document');
      }

      const data: { document: DocDetail } = await response.json();
      setEditTitle(data.document.title);
      setEditType(data.document.type);
      setEditContent(data.document.content);
    } catch (error) {
      console.error(error);
      setEditError('Failed to load document');
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    setEditOpen(false);
    setEditId(null);
    setEditTitle('');
    setEditContent('');
    setEditType('general');
    setEditError('');
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;

    setEditLoading(true);
    setEditError('');

    try {
      const response = await fetch(`/api/business-docs/${editId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId,
          title: editTitle,
          content: editContent,
          type: editType,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update document');
      }

      await response.json();
      closeEditModal();
      await fetchDocuments();
    } catch (error) {
      console.error(error);
      setEditError('Failed to update document');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;

    try {
      const response = await fetch(`/api/business-docs/${docId}?businessId=${businessId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      await response.json();
      await fetchDocuments();
    } catch (error) {
      console.error(error);
      setDocsError('Failed to delete document');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold mb-4">Knowledge Base Documents</h3>
      <p className="text-gray-600 mb-4">
        Upload documents to help Mia answer questions about your business.
      </p>

      {message && (
        <div className={`mb-4 p-3 rounded ${message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Document Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Pricing Sheet 2024"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder-gray-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Document Type
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
          >
            <option value="general">General Information</option>
            <option value="faq">FAQ</option>
            <option value="pricing">Pricing</option>
            <option value="policy">Policy</option>
            <option value="service">Services</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Paste your document content here..."
            rows={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder-gray-500"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Tip: Copy/paste from Word, PDF, or type directly. Mia will use this to answer customer questions.
          </p>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Uploading...' : 'Upload to Knowledge Base'}
        </button>
      </form>

      <div className="mt-8">
        <h4 className="text-md font-semibold mb-2">Your Documents</h4>
        {docsError && (
          <div className="mb-3 p-3 rounded bg-red-100 text-red-800">
            {docsError}
          </div>
        )}
        {docsLoading ? (
          <p className="text-gray-500">Loading documents...</p>
        ) : documents.length === 0 ? (
          <p className="text-gray-500">No documents uploaded yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200 border border-gray-200 rounded">
            {documents.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-gray-900 font-medium">{doc.title}</p>
                  <p className="text-xs text-gray-500">{doc.type}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openEditModal(doc.id)}
                    className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(doc.id)}
                    className="px-3 py-1.5 text-sm rounded border border-red-300 text-red-600 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold">Edit Document</h4>
              <button
                type="button"
                onClick={closeEditModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {editError && (
              <div className="mb-3 p-3 rounded bg-red-100 text-red-800">
                {editError}
              </div>
            )}

            {editLoading ? (
              <p className="text-gray-500">Loading...</p>
            ) : (
              <form onSubmit={handleEditSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={editType}
                    onChange={(e) => setEditType(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  >
                    <option value="general">General Information</option>
                    <option value="faq">FAQ</option>
                    <option value="pricing">Pricing</option>
                    <option value="policy">Policy</option>
                    <option value="service">Services</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                    required
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {editLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
